import React, { useState, useMemo, useEffect } from "react";
import { UseCompany } from "../../hooks/company";
import { useDispatch } from "react-redux";
import { updateCompany } from "../../redux/company/actions";
import { useForm } from "react-hook-form";

import SoftBox from "../../components/SoftBox";
// import SoftTypography from "../../components/SoftTypography";
import { Grid } from "@mui/material";
import SoftInput from "../../components/SoftInput";
import LightSelect from "../CompanyView/LightSelect";
import PageHeader from "../../components/PageHeader";

import toast from "react-hot-toast";
// import { stepperPageStyles } from "../CreateCompany/stepperPageStyles";
import staticIndustries from "../../constants/industries";
import { UseUserDetails } from "../../hooks/auth";
import { updateUserDetails } from "../../redux/auth/actions";
import { GET_INDUSTRIES } from '../../api/industry';

// Embedded tab
import Automations from "../Automations";

// Services
import { PUT_USER } from "../../api/user";

export default function CompanySettings() {
  const company = UseCompany();
  const user = UseUserDetails();
  const dispatch = useDispatch();
  // const classes = stepperPageStyles();

  // Detect "demo" user from user profile
  // Note: Demo accounts have companyName and industry fields in the user object
  const isDemoUser = useMemo(() => {
    return user?.industry && user?.companyName
  }, [user]);

  const { register, setValue, handleSubmit, watch, formState: { errors },
  getValues,
  setError,
  clearErrors,
  trigger,
  } = useForm({
    defaultValues: {
      ...company,
      // Use "demo" account companyName and industry, if available
      businessName: isDemoUser ? user?.companyName : company?.businessName,
      industry: isDemoUser ? user?.industry : company?.industry,
    },
  });

  const onSubmit = async (data) => {
    if (isDemoUser) {
     try {
      // Update companyName and industry in the "user" profile
      await PUT_USER(user?._id, {
        companyName: data?.businessName,
        industry: data?.industry
      });

      dispatch(updateUserDetails({
        companyName: data?.businessName,
        industry: data?.industry
      }));

      toast.success("Successfully Updated Company Details");
     } catch (err) {
      toast.error(err.message);
     }
    } else {
      // Update companyName and industry in the "company" profile
      dispatch(updateCompany(data));
      toast.success("Successfully Updated Company Details");
    }
  };

  // LightSelect is a plain div/button control, not a native input, so its
  // value is tracked through the setValue() call in its onChange rather than
  // a DOM ref — register it here for the "required" rule without spreading
  // register's ref/onChange props onto it.
  register("industry", { required: true });

  const [industryOptions, setIndustryOptions] = useState([]);

  const fallbackIndustryOptions = staticIndustries.map((name) => ({ value: name, label: name }));

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const industries = await GET_INDUSTRIES();
        const businessIndustries = (industries || []).map(industry => ({
          value: industry.name,
          label: industry.name,
        }));
        // The list this endpoint serves is still being filled in on the
        // backend — fall back to the static list rather than leaving the
        // dropdown with nothing to pick from.
        setIndustryOptions(businessIndustries.length > 0 ? businessIndustries : fallbackIndustryOptions);
      } catch (fetchError) {
        console.error('Failed to fetch industries', fetchError);
        setIndustryOptions(fallbackIndustryOptions);
      }
    };
  
    fetchIndustries();
  }, []);

  const checkPassword = async () => {
    const newPass = getValues("newPassword");
    const confPass = getValues("confirmPassword");
    const result = await trigger("confirmPassword");
    if (!result) return;
    if (newPass !== confPass) {
      setError("confirmPassword", {
        type: "custom",
        message: `Password not match!`,
      });
      return;
    }else {
      clearErrors("confirmPassword");
    }
  };

  const [tabValue, setTabValue] = useState(0);

  return (
    <>
      <PageHeader headerText="Company Setup" subheaderText="Profile your company and configure how it runs."  />
      <SoftBox sx={{ margin: { mini: "1rem", sm: "1.5rem" } }}>
        <Grid container>
          <Grid
              className="rounded-lg p-4"
              style={{ backgroundColor: 'white' }}
               item mini={12}>
              {/* Tabs first — they're the primary navigation between modes.
                  Putting them above the title avoids the awkward "Profile"
                  tab sitting under a "Performance alerts" heading. */}
              <Grid container>
                <Grid item mini={12}>
                  <SoftBox
                    sx={{
                      display: "flex",
                      gap: "24px",
                      borderBottom: "1px solid #E5E9EB",
                      marginBottom: "20px",
                    }}
                  >
                    {[
                      { value: 0, label: "Profile" },
                      { value: 1, label: "Automations" },
                    ].map((opt) => {
                      const active = tabValue === opt.value;
                      return (
                        <SoftBox
                          key={opt.value}
                          component="button"
                          type="button"
                          onClick={() => setTabValue(opt.value)}
                          sx={{
                            background: "transparent",
                            border: 0,
                            padding: "8px 0",
                            marginBottom: "-1px",
                            fontFamily: "DM Sans",
                            fontSize: "14px",
                            fontWeight: active ? 600 : 500,
                            color: active ? "#04549C" : "#6C757D",
                            cursor: "pointer",
                            borderBottom: active
                              ? "2px solid #04549C"
                              : "2px solid transparent",
                            transition: "color 120ms ease, border-color 120ms ease",
                            "&:hover": active ? undefined : { color: "#344054" },
                          }}
                        >
                          {opt.label}
                        </SoftBox>
                      );
                    })}
                  </SoftBox>
                </Grid>
                {/* Contextual header for the active tab — sits BELOW the
                    tabs so the navigation feels primary and the title
                    reads as "you are here". */}
                <Grid item mini={12} sx={{ marginBottom: "8px" }}>
                  <Grid container alignItems="center">
                    <Grid item mini={6}>
                      <p
                        style={{
                          fontFamily: 'DM Sans, Inter, sans-serif',
                          color: '#191D23',
                          fontSize: '18px',
                          fontWeight: 600,
                          margin: 0,
                        }}
                      >
                        {tabValue === 0 ? "Profile" : "Automations"}
                      </p>
                      <p
                        style={{
                          fontFamily: 'DM Sans, Inter, sans-serif',
                          color: '#667085',
                          fontSize: '13.5px',
                          marginTop: '3px',
                          marginBottom: 0,
                        }}
                      >
                        {tabValue === 0
                          ? "Edit and update your profile for better performance."
                          : "Little rules that run themselves — alerts, reminders and rewards."}
                      </p>
                    </Grid>
                    <Grid item mini={6} className="text-end">
                      {tabValue === 0 && (
                        <button
                            type="submit"
                            form="compForm"
                            className="border px-4 py-1 rounded text-white"
                            style={{ backgroundColor: company.primaryColor }}
                          >
                          Update
                        </button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                {tabValue === 0 && (
                  <form id="compForm" onSubmit={handleSubmit(onSubmit)}>
                    <SoftBox className="mt-5">
                      <Grid container className="mt-3">
                        <Grid item mini={4}>
                        <p style={{ color: '#000000', fontSize: '22px', fontWeight: '700' }}>Business Details</p>
                          <p>This section provides essential information about the company's official business name. The business name is a fundamental aspect of the company's identity, serving as a unique identifier in the business world</p>
                        </Grid>
                        <Grid item mini={2}>{/* blank */}</Grid>
                        <Grid item mini={6}>
                          <SoftInput
                            id="companyBusinessName"
                            type="name"
                            className="shadow-sm rounded-lg"
                            placeholder="E.g. GameMyBiz"
                            {...register("businessName", { required: true })}
                            size="large"
                          />
                          <div className="mt-3">
                            <LightSelect
                              width="100%"
                              height={48}
                              fontSize={14}
                              radius={8}
                              options={industryOptions}
                              value={watch("industry")}
                              onChange={(option) => setValue("industry", option.value, { shouldValidate: true, shouldDirty: true })}
                              placeholder="Select your industry"
                            />
                          </div>
                        </Grid>
                      </Grid>
                      <hr className="my-3 divide-slate-300 mt-5 mb-4" />
                      <Grid container>
                        <Grid item mini={3}>
                          <p style={{ color: '#000000', fontSize: '22px', fontWeight: '700' }}>Business Address</p>
                          <p>The business address is a pivotal piece of information providing users with the physical location of the company. This includes the street address, city, state, and postal code</p>
                        </Grid>
                        <Grid item mini={3}>{/* blank */}</Grid>
                        <Grid item mini={6}>
                          <SoftBox
                          sx={{
                            display: "flex",
                            gap: 4,
                            flex: "1 1 0%",
                            marginBottom: "0.75rem",
                          }}
                        >
                          <SoftBox sx={{ flex: "1 1 0%" }}>
                            <SoftInput
                              id="companyCountry"
                              type="name"
                              className="shadow-sm rounded-lg"
                              placeholder="E.g. USA"
                              {...register("country", { required: true })}
                              size="large"
                              disabled={isDemoUser}
                            />
                          </SoftBox>
                          <SoftBox sx={{ flex: "1 1 0%" }}>
                            <SoftInput
                              id="companyStateRegion"
                              type="name"
                              className="shadow-sm rounded-lg"
                              placeholder="E.g. Idaho, USA"
                              {...register("state", { required: true })}
                              size="large"
                              disabled={isDemoUser}
                            />
                          </SoftBox>
                          </SoftBox>
                          <SoftBox
                            sx={{
                              display: "flex",
                              gap: 4,
                              flex: "1 1 0%",
                              marginBottom: "0.75rem",
                            }}
                          >
                          <SoftBox sx={{ flex: "1 1 0%" }}>
                            <SoftInput
                              id="companyStreetAddress"
                              type="name"
                              className="shadow-sm rounded-lg"
                              placeholder="E.g. 123 Bear Lake Ave"
                              {...register("addressOne", { required: true })}
                              size="large"
                              disabled={isDemoUser}
                            />
                          </SoftBox>
                          <SoftBox sx={{ flex: "1 1 0%" }}>
                            <SoftInput
                              id="addressLineTwo"
                              type="name"
                              className="shadow-sm rounded-lg"
                              placeholder="Address Line 2 (optional)"
                              {...register("addressTwo", { required: false })}
                              size="large"
                              disabled={isDemoUser}
                            />
                          </SoftBox>
                          </SoftBox>
                          <SoftBox
                            sx={{
                              display: "flex",
                              gap: 4,
                              flex: "1 1 0%",
                              marginBottom: "0.75rem",
                            }}
                          >
                            <SoftBox sx={{ flex: "1 1 0%" }}>
                              <SoftInput
                                id="companyTownCity"
                                type="name"
                                className="shadow-sm rounded-lg"
                                placeholder="E.g. Pocatello"
                                {...register("city", { required: true })}
                                size="large"
                                disabled={isDemoUser}
                              />
                            </SoftBox>
                            <SoftBox sx={{ flex: "1 1 0%" }}>
                              <SoftInput
                                id="companyZipcode"
                                type="name"
                                className="shadow-sm rounded-lg"
                                placeholder="E.g. 89123"
                                {...register("zipcode", { required: true })}
                                size="large"
                                disabled={isDemoUser}
                              />
                            </SoftBox>
                          </SoftBox>
                        </Grid>
                      </Grid>
                      <hr className="my-3 divide-slate-300 mt-5 mb-4" />
                      <Grid container>
                        <Grid item mini={3}>
                          <p style={{ color: '#000000', fontSize: '22px', fontWeight: '700' }}>Business Contact details</p>
                          <p>This section contains essential contact information for the business, facilitating seamless communication between stakeholders</p>
                        </Grid>
                        <Grid item mini={3}>{/* blank */}</Grid>
                        <Grid item mini={6}>
                          <SoftBox sx={{ display: "flex", gap: 4, flex: "1 1 0%" }}>
                            <SoftBox sx={{ flex: "1 1 0%" }}>
                              <SoftInput
                                id="companyPhoneNum"
                                type="tel"
                                placeholder="E.g. 912-123-8564"
                                format="###-###-####"
                                className="shadow-sm rounded-lg"
                                {...register("phoneNumber", { required: true })}
                                size="large"
                                disabled={isDemoUser}
                              />
                            </SoftBox>
                            <SoftBox sx={{ flex: "1 1 0%" }}>
                              <SoftInput
                                id="companyWebsite"
                                type="name"
                                className="shadow-sm rounded-lg"
                                placeholder="E.g. www.gamemybiz.com"
                                {...register("websiteUrl", { required: true })}
                                size="large"
                                disabled={isDemoUser}
                              />
                            </SoftBox>
                          </SoftBox>
                        </Grid>
                      </Grid>
                    </SoftBox>
                  </form>
                )}
                {tabValue === 1 && (
                  <Grid item mini={12} sx={{ marginTop: "1.5rem", width: "100%" }}>
                    <Automations />
                  </Grid>
                )}
              </Grid>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
}
